import type { ICampaign } from '@bingads-webui/campaign-odata-schemas/types/schema';
import _ from 'underscore';

type CampaignTypeInput = ICampaign['CampaignType'] | string | number;
type CampaignSubTypeInput = NonNullable<ICampaign['CampaignSubType']> | string | number;

// TODO: Move the constants to a separate package in Shared Components so that with-site-map can consume CampaignTypeMapping from it
// Currently a similar CampaignTypeMapping is exported from with-site-map, but the package has too many unrelated dependencies
export const CampaignTypeMapping = {
  None: 0,
  Default: 1 << 0,
  Shopping: 1 << 1,
  DynamicSearchAds: 1 << 2,
  Audience: 1 << 3,
  Smart: 1 << 4,
  Hotel: 1 << 5,
  PerformanceMax: 1 << 6,
  App: 1 << 7,
  LinkedIn: 1 << 8,
  ObjectiveBased: 1 << 9,
  All: 0xFFFF,
};

export const CampaignTypes = {
  Default: 'Default', // Default is search
  Shopping: 'Shopping',
  DynamicSearchAds: 'DynamicSearchAds',
  Audience: 'Audience',
  Smart: 'Smart',
  Hotel: 'Hotel',
  PerformanceMax: 'PerformanceMax',
  App: 'App',
  LinkedIn: 'LinkedIn',
  ObjectiveBased: 'ObjectiveBased',
};

export const CampaignSubTypes = {
  AudienceStatic: 'AudienceStatic',
  AudienceFeedBased: 'AudienceFeedBased',
  AudienceDynamicFeedBased: 'AudienceDynamicFeedBased',
  AudienceVideoAds: 'AudienceVideoAds',
  BrandAwarenessVideoAds: 'BrandAwarenessVideoAds',
  DisplayAds: 'DisplayAds',
  Brand: 'Brand',
  ShoppingCoOperative: 'ShoppingCoOperative',
  ShoppingSponsoredProductAd: 'ShoppingSponsoredProductAd',
  ShoppingSmartAds: 'ShoppingSmartAds',
  PerformanceMaxApp: 'PerformanceMaxApp',
  PerformanceMaxXbox: 'PerformanceMaxXbox',
};

export const CampaignSubTypeMapping = {
  [CampaignSubTypes.AudienceStatic]: 0,
  [CampaignSubTypes.AudienceFeedBased]: 1,
  [CampaignSubTypes.AudienceDynamicFeedBased]: 2,
  [CampaignSubTypes.AudienceVideoAds]: 3,
  [CampaignSubTypes.BrandAwarenessVideoAds]: 4,
  [CampaignSubTypes.DisplayAds]: 5,
  [CampaignSubTypes.Brand]: 6,
  [CampaignSubTypes.ShoppingCoOperative]: 10,
  [CampaignSubTypes.ShoppingSponsoredProductAd]: 11,
  [CampaignSubTypes.ShoppingSmartAds]: 12,
  [CampaignSubTypes.PerformanceMaxApp]: 31,
  [CampaignSubTypes.PerformanceMaxXbox]: 32,
};

export const CampaignSubTypeTextMapping = _.invert(CampaignSubTypeMapping);

export const PMaxAudienceExclusionMode = 'pmax-exclusions';

export const PMaxAudienceExclusionSupportedTypes = [
  'Remessaging',
  'Product',
  'RemarketingSimilar',
  'ImpressionBasedRemarketing',
  'CustomerList',
  'Custom',
  'CombinedList',
  'InMarket',
] as const;

type PMaxAudienceExclusionsEligibility = {
  permissions?: any;
  campaignType?: CampaignTypeInput;
  campaignSubType?: CampaignSubTypeInput | null;
  scope?: string | number | null;
};

export const isPerformanceMaxCampaignType = (campaignType?: CampaignTypeInput) =>
  campaignType === CampaignTypes.PerformanceMax ||
  campaignType === CampaignTypeMapping.PerformanceMax ||
  campaignType === 6;

const isUnsupportedPMaxAudienceExclusionSubType = (campaignSubType?: CampaignSubTypeInput | null) =>
  campaignSubType === CampaignSubTypes.PerformanceMaxApp ||
  campaignSubType === CampaignSubTypeMapping[CampaignSubTypes.PerformanceMaxApp] ||
  campaignSubType === CampaignSubTypes.PerformanceMaxXbox ||
  campaignSubType === CampaignSubTypeMapping[CampaignSubTypes.PerformanceMaxXbox];

export const isPMaxAudienceExclusionsEligible = ({
  permissions,
  campaignType,
  campaignSubType,
  scope,
}: PMaxAudienceExclusionsEligibility) => {
  const dynamicPermissions = permissions?.DynamicPermissions
    || permissions?.dynamic
    || permissions?.Permissions?.dynamic
    || {};

  return dynamicPermissions.PMaxCampaignLevelAudienceCriterion === true
    && isPerformanceMaxCampaignType(campaignType)
    && !isUnsupportedPMaxAudienceExclusionSubType(campaignSubType)
    && (scope === 'Campaign' || scope === 2);
};

export const isPMaxAudienceExclusionRow = (item: any) =>
  item?.Level === 'Campaign'
  && item?.Status === 'Excluded'
  && isPerformanceMaxCampaignType(item?.Campaign?.CampaignType)
  && !isUnsupportedPMaxAudienceExclusionSubType(item?.Campaign?.CampaignSubType);

export const isXandrCampaign = (campaignSubType: CampaignSubTypeInput) =>
  campaignSubType === CampaignSubTypes.DisplayAds ||
  campaignSubType === CampaignSubTypes.BrandAwarenessVideoAds ||
  campaignSubType === CampaignSubTypeMapping.DisplayAds ||
  campaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds;

export const isAudienceNativeAdsCampaign = (campaignSubType: CampaignSubTypeInput) =>
  campaignSubType === CampaignSubTypes.AudienceStatic ||
  campaignSubType === CampaignSubTypeMapping.AudienceStatic;

// TODO remove for SubtypeCleanup
export const isXandrDisplayAdsCampaign = (campaignSubType: CampaignSubTypeInput) =>
  campaignSubType === CampaignSubTypes.DisplayAds ||
  campaignSubType === CampaignSubTypeMapping.DisplayAds;

// TODO remove for SubtypeCleanup
export const isXandrVideoAdsCampaign = (campaignSubType: CampaignSubTypeInput) =>
  campaignSubType === CampaignSubTypes.BrandAwarenessVideoAds ||
  campaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds;

export const isSearchShoppingCampaign = (campaignType: CampaignTypeInput, campaignSubType: CampaignSubTypeInput) => {
  const isShoppingType = campaignType === CampaignTypes.Shopping || campaignType === CampaignTypeMapping.Shopping;
  const isSearchShopping = isShoppingType && !campaignSubType;

  return isSearchShopping;
};

export const isSearchCampaign = (campaignType: CampaignTypeInput) =>
  campaignType === CampaignTypes.Default ||
  campaignType === CampaignTypeMapping.Default;

export const isShoppingCampaign = (campaignType: CampaignTypeInput) =>
  campaignType === CampaignTypes.Shopping ||
  campaignType === CampaignTypeMapping.Shopping;

export const isAudienceCampaign = (campaignType: CampaignTypeInput) =>
  campaignType === CampaignTypes.Audience ||
  campaignType === CampaignTypeMapping.Audience;

export const isLinkedInCampaign = (campaignType: CampaignTypeInput) =>
  campaignType === CampaignTypes.LinkedIn ||
  campaignType === CampaignTypeMapping.LinkedIn;

export const isAppCampaign = (campaignType: CampaignTypeInput) =>
  campaignType === CampaignTypes.App ||
  campaignType === CampaignTypeMapping.App;

type Campaign = Omit<Pick<ICampaign, 'CampaignSubType' | 'BiddingScheme' | 'IsDealCampaign'>, 'CampaignSubType' | 'BiddingScheme'> & {
  CampaignSubType: CampaignSubTypeInput;
  BiddingScheme: (NonNullable<ICampaign['BiddingScheme']> & {
    Type: NonNullable<NonNullable<ICampaign['BiddingScheme']>['Type']> | string;
  }) | null;
};

export const isAudienceCTVCampaign = (campaign: Campaign) =>
  isXandrVideoAdsCampaign(campaign.CampaignSubType) && campaign.BiddingScheme && campaign.BiddingScheme.Type === 'ManualCpv';

export const isAudienceDealCampaign = (campaign: Campaign) =>
  isXandrVideoAdsCampaign(campaign.CampaignSubType) && campaign.IsDealCampaign;

export const isAudienceShoppingCampaign = (campaignType: CampaignTypeInput, campaignSubType: CampaignSubTypeInput) => {
  const isAudienceType = campaignType === CampaignTypes.Audience || campaignType === CampaignTypeMapping.Audience;
  const isAudienceShopping = isAudienceType && (
    campaignSubType === CampaignSubTypes.AudienceFeedBased ||
    campaignSubType === CampaignSubTypes.AudienceDynamicFeedBased ||
    campaignSubType === CampaignSubTypeMapping.AudienceFeedBased ||
    campaignSubType === CampaignSubTypeMapping.AudienceDynamicFeedBased
  );

  return isAudienceShopping;
};

export const isSearchShoppingOrAudienceShoppingCampaign =
  (campaignType: CampaignTypeInput, campaignSubType: CampaignSubTypeInput) =>
    isAudienceShoppingCampaign(campaignType, campaignSubType) || isSearchShoppingCampaign(campaignType, campaignSubType);

export enum ProductGroupSubTypeEnum {
  standard = 1,
  coop = 2,
  all = 3,
}

export const isXandrServing = (campaignSubType: CampaignSubTypeInput) => isXandrCampaign(campaignSubType);

// @aiContributed-MAPFuse-2026-03-11
export const isBrandCampaign = (campaignSubType: CampaignSubTypeInput) =>
  campaignSubType === CampaignSubTypes.Brand ||
  campaignSubType === CampaignSubTypeMapping[CampaignSubTypes.Brand];

export const isVideoCapableCampaign = (campaignSubType: CampaignSubTypeInput) =>
  campaignSubType === CampaignSubTypes.AudienceVideoAds ||
  campaignSubType === CampaignSubTypes.BrandAwarenessVideoAds ||
  campaignSubType === CampaignSubTypes.Brand ||
  campaignSubType === CampaignSubTypeMapping[CampaignSubTypes.AudienceVideoAds] ||
  campaignSubType === CampaignSubTypeMapping[CampaignSubTypes.BrandAwarenessVideoAds] ||
  campaignSubType === CampaignSubTypeMapping[CampaignSubTypes.Brand];

export const isAnyBrandType = (campaignSubType: CampaignSubTypeInput) =>
  isXandrCampaign(campaignSubType) || isBrandCampaign(campaignSubType);

export const isVideoRequiredCampaign = (campaignSubType: CampaignSubTypeInput) =>
  isVideoCapableCampaign(campaignSubType);
